/**
 * Data for the Ruby AI tabs: the catalogue (pos-agents-ext), the AI package
 * family with its tiers (super-admin-ext2), and — for price estimates —
 * the last 30 days of spend. Each loads independently: a failed family or
 * usage fetch degrades its columns to "—", never the whole tab.
 */
import { useCallback, useEffect, useState } from 'react';
import { AI_FAMILY_ID as ST_AI_FAMILY_ID } from '@platform/shared-types';
import { api } from '../../../services/apiService';
import {
  getRubyAiCatalog, getRubyAiUsage, rubyAiErrorText,
  type AiCatalogResponse, type RubyAiUsageResponse,
} from '../../../services/aiControl/rubyAi';
import type { FamilyTier } from './catalogModel';

// Guarded runtime import (stale Vite pre-bundle → undefined). The id itself
// is part of the D13 contract; the TIERS are always read from the record.
export const AI_FAMILY_ID: string = typeof ST_AI_FAMILY_ID === 'string' && ST_AI_FAMILY_ID
  ? ST_AI_FAMILY_ID
  : (console.error('@platform/shared-types exported no AI_FAMILY_ID — RESTART the dev server (npx vite --force).'), 'fam_ruby_ai');

export interface AiFamily {
  family: { id: string; name: string; tierIds: string[]; defaultTierId?: string; noticeDays?: number; enrollment?: string };
  tiers: FamilyTier[];
  missingPackageIds: string[];
}

export function useRubyAiCatalog() {
  const [catalog, setCatalog] = useState<AiCatalogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      setCatalog(await getRubyAiCatalog());
      setError(null);
    } catch (e) {
      setError(rubyAiErrorText(e, 'Failed to load the Ruby AI catalogue'));
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  return { catalog, error, reload, setCatalog };
}

export function useAiFamily() {
  const [family, setFamily] = useState<AiFamily | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const r = await api.packageFamilies.get(AI_FAMILY_ID);
      setFamily({ family: r.family, tiers: r.tiers ?? [], missingPackageIds: r.missingPackageIds ?? [] });
      setError(null);
    } catch (e) {
      setError(rubyAiErrorText(e, 'Failed to load the Ruby AI package family'));
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  return { family, error, reload };
}

const DAY = 86_400_000;

/** Spend for estimates. `range` absent → last 30 days. */
export function useRubyAiUsage(range?: { fromIso?: string; toIso?: string }) {
  const [usage, setUsage] = useState<RubyAiUsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const from = range?.fromIso;
  const to = range?.toIso;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const r = from || to ? { fromIso: from, toIso: to } : { fromIso: new Date(Date.now() - 30 * DAY).toISOString(), toIso: new Date().toISOString() };
    getRubyAiUsage(r)
      .then((u) => { if (!cancelled) { setUsage(u); setError(null); } })
      .catch((e) => { if (!cancelled) setError(rubyAiErrorText(e, 'Failed to load usage')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);
  return { usage, error, loading };
}
